import messagesInEnglish from 'app/lang/en.json';
import messagesInEnglishGB from 'app/lang/en-GB.json';
import messagesInEnglishUS from 'app/lang/en-US.json';
import messagesInGerman from 'app/lang/de-DE.json';
import messagesInSpanish from 'app/lang/es-ES.json';
import messagesInFrenchCA from 'app/lang/fr-CA.json';
import messagesInFrenchFR from 'app/lang/fr-FR.json';
import messagesInItalian from 'app/lang/it-IT.json';
import messagesInNorwegian from 'app/lang/nb-NO.json';
import messagesInDutch from 'app/lang/nl-NL.json';
import messagesInPolish from 'app/lang/pl.json';
import messagesInPortuguese from 'app/lang/pt-PT.json';
import messagesInHungarian from 'app/lang/hu-HU.json';
import messagesInSlovak from 'app/lang/sk-SK.json';
import messagesInEnglishCA from 'app/lang/en-CA.json';
import messagesInEnglishIE from 'app/lang/en-IE.json';
import messagesInEnglishAU from 'app/lang/en-AU.json';
import messagesInEnglishIN from 'app/lang/en-IN.json';
import messagesInEnglishNZ from 'app/lang/en-NZ.json';
import { flatten } from 'app/shared/utils/flatten';
import { normalizeLocale } from '@hqo/date-time/core';
import { useMemo } from 'react';

export const DEFAULT_LOCALE = 'en';

// Locale codes are lowercased before lookup (intl-provider calls
// `configLocale.toLowerCase()`), so map keys must match.
const messagesMap = new Map<string, Record<string, unknown>>([
  ['en', { ...messagesInEnglish.baseMiniApp }],
  ['en-gb', { ...messagesInEnglishGB.baseMiniApp }],
  ['en-us', { ...messagesInEnglishUS.baseMiniApp }],
  ['en-ca', { ...messagesInEnglishCA.baseMiniApp }],
  ['en-ie', { ...messagesInEnglishIE.baseMiniApp }],
  ['en-au', { ...messagesInEnglishAU.baseMiniApp }],
  ['en-in', { ...messagesInEnglishIN.baseMiniApp }],
  ['en-nz', { ...messagesInEnglishNZ.baseMiniApp }],
  ['de-de', { ...messagesInGerman.baseMiniApp }],
  ['es-es', { ...messagesInSpanish.baseMiniApp }],
  ['fr-ca', { ...messagesInFrenchCA.baseMiniApp }],
  ['fr-fr', { ...messagesInFrenchFR.baseMiniApp }],
  ['it-it', { ...messagesInItalian.baseMiniApp }],
  ['nb-no', { ...messagesInNorwegian.baseMiniApp }],
  ['nl-nl', { ...messagesInDutch.baseMiniApp }],
  ['pl', { ...messagesInPolish.baseMiniApp }],
  ['pl-pl', { ...messagesInPolish.baseMiniApp }],
  ['pt-pt', { ...messagesInPortuguese.baseMiniApp }],
  ['hu', { ...messagesInHungarian.baseMiniApp }],
  ['hu-hu', { ...messagesInHungarian.baseMiniApp }],
  ['sk', { ...messagesInSlovak.baseMiniApp }],
  ['sk-sk', { ...messagesInSlovak.baseMiniApp }],
]);

// A structurally invalid tag reaches Intl only via the bare-language fallback: an input like
// `hu_` or `hu__HU` normalizes to `hu-` / `hu--hu`, which still matches the bare `hu` alias but is
// not a usable BCP-47 tag, so `Intl.NumberFormat(locale)` would throw a RangeError at render time.
// Keep the full normalized tag only when Intl can consume it; otherwise fall back to the bare
// language, which always can.
const isIntlSafe = (tag: string): boolean => {
  try {
    return Boolean(new Intl.NumberFormat(tag));
  } catch {
    return false;
  }
};

export const getAvailableLocale = (userLocale: string): [string, string] => {
  // Guard before normalizing: `normalizeLocale('')` resolves empty input to the package's OWN default
  // (`en-US`), but here an absent locale must fall to this app's `DEFAULT_LOCALE` (`en`).
  if (!userLocale.trim()) {
    return [DEFAULT_LOCALE, DEFAULT_LOCALE];
  }

  // `normalizeLocale` (@hqo/date-time) canonicalizes case + separator AND strips the POSIX codeset /
  // modifier (`hu_HU.UTF-8` -> `hu-HU`) that the old hand-rolled `_`->`-` pass left behind — those used
  // to lose their region. Lower-case it for the lookup because the map keys are lower-case (VIS-1695).
  const normalized = normalizeLocale(userLocale).toLowerCase();

  if (messagesMap.has(normalized)) {
    return [normalized, normalized];
  }

  const [lang] = normalized.split('-');

  if (messagesMap.has(lang)) {
    return [lang, isIntlSafe(normalized) ? normalized : lang];
  }

  return [DEFAULT_LOCALE, DEFAULT_LOCALE];
};

interface LocaleData {
  messages: Record<string, string>;
  locale: string;
}

export const useLocaleData = (userLocale: string): LocaleData => useMemo(() => {
  const [messagesKey, locale] = getAvailableLocale(userLocale);

  return {
    locale,
    messages: flatten(messagesMap.get(messagesKey)) as Record<string, string>,
  };
}, [userLocale]);
